import React, { useState, useCallback, useMemo } from "react";
import {
    Box,
    Typography,
    Button,
    IconButton,
    List,
    ListItem,
    ListItemButton,
    ListItemText,
    Chip,
    Collapse,
    Menu,
    MenuItem,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    FormControl,
    InputLabel,
    Select,
    Alert,
    useTheme,
    useMediaQuery,
} from "@mui/material";
import {
    Add as AddIcon,
    Delete as DeleteIcon,
    Edit as EditIcon,
    ContentCopy as CopyIcon,
    MoreVert as MoreIcon,
    ExpandMore as ExpandIcon,
    ExpandLess as CollapseIcon,
    DragIndicator as DragIcon,
} from "@mui/icons-material";
import {
    DndContext,
    closestCenter,
    KeyboardSensor,
    PointerSensor,
    useSensor,
    useSensors,
} from "@dnd-kit/core";
import {
    arrayMove,
    SortableContext,
    sortableKeyboardCoordinates,
    verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { capitalizeFirstLetter } from "Constant/string";

const removeUnderScores = (str) => {
    return str.replace(/_/g, " ");
};
// SortableItem component for drag and drop functionality
const SortableItem = ({
    step,
    index,
    isSelected,
    status,
    getStatusColor,
    handleSelectStep,
    handleContextMenu,
}) => {
    const {
        attributes,
        listeners,
        setNodeRef,
        transform,
        transition,
        isDragging,
    } = useSortable({ id: step.id });

    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.5 : 1,
        zIndex: isDragging ? 1000 : "auto",
    };

    return (
        <ListItem
            ref={setNodeRef}
            style={style}
            sx={{
                borderLeft: isSelected ? "3px solid" : "3px solid transparent",
                borderColor: isSelected ? "warning.main" : "transparent",
                bgcolor: isSelected ? "primary.50" : "transparent",
                "&:hover": {
                    bgcolor: isSelected ? "primary.100" : "grey.50",
                },
            }}
        >
            <ListItemButton
                onClick={() => handleSelectStep(step.id)}
                onContextMenu={(e) => handleContextMenu(e, step.id)}
                sx={{ pl: 1 }}
            >
                {/* Drag Handle */}
                <IconButton
                    size="small"
                    sx={{
                        mr: 1,
                        cursor: "grab",
                        color: "text.secondary",
                        "&:hover": {
                            color: "primary.main",
                            bgcolor: "primary.50",
                        },
                        "&:active": {
                            cursor: "grabbing",
                        },
                    }}
                    {...attributes}
                    {...listeners}
                >
                    <DragIcon fontSize="small" />
                </IconButton>

                <ListItemText
                    primary={
                        <Box
                            sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 1,
                            }}
                        >
                            <Typography variant="body2" noWrap>
                                {step.title || `Step ${index + 1}`}
                            </Typography>
                            <Chip
                                label={status}
                                size="small"
                                color={getStatusColor(status)}
                                variant="outlined"
                                sx={{
                                    fontSize: "0.7rem",
                                    height: 20,
                                }}
                                onClick={() => {}}
                            />
                        </Box>
                    }
                    secondary={
                        <Typography
                            variant="caption"
                            color="text.secondary"
                            noWrap
                        >
                            {capitalizeFirstLetter(
                                removeUnderScores(step.type),
                            )}{" "}
                            • {step.optional ? "Optional" : "Required"}
                        </Typography>
                    }
                />

                <IconButton
                    size="small"
                    onClick={(e) => {
                        e.stopPropagation();
                        handleContextMenu(e, step.id);
                    }}
                >
                    <MoreIcon fontSize="small" />
                </IconButton>
            </ListItemButton>
        </ListItem>
    );
};

const StepManager = ({
    steps = [],
    onAddStep,
    onEditStep,
    onDeleteStep,
    onReorderSteps,
    selectedStepId,
    onSelectStep,
    isPreviewMode,
    onTogglePreview,
    onDuplicateStep,
    templates,
}) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
    const funnelTemplate = useMemo(() => {
        return templates?.data?.find((template) => template?.name === "funnel");
    }, [templates]);
    const options = useMemo(() => {
        return funnelTemplate?.fields?.find((field) => field?.name === "steps")
            ?.options;
    }, [funnelTemplate]);
    const [expandedSections, setExpandedSections] = useState({
        steps: true,
        settings: false,
    });
    const [anchorEl, setAnchorEl] = useState(null);
    const [contextStepId, setContextStepId] = useState(null);
    const [addStepDialog, setAddStepDialog] = useState(false);
    const [newStepType, setNewStepType] = useState("button");
    const [error, setError] = useState(null);

    // Drag and drop sensors
    const sensors = useSensors(
        useSensor(PointerSensor),
        useSensor(KeyboardSensor, {
            coordinateGetter: sortableKeyboardCoordinates,
        }),
    );

    // Handle drag end for reordering steps
    const handleDragEnd = useCallback(
        (event) => {
            const { active, over } = event;

            if (active.id !== over?.id) {
                const oldIndex = steps.findIndex(
                    (step) => step.id === active.id,
                );
                const newIndex = steps.findIndex((step) => step.id === over.id);

                const newSteps = arrayMove(steps, oldIndex, newIndex);

                if (onReorderSteps) {
                    onReorderSteps(newSteps);
                }
            }
        },
        [steps, onReorderSteps],
    );

    const getStepIcon = (type) => {
        const iconMap = {
            button: "🔘",
            input: "📝",
            multiselect: "☑️",
            slider: "🎚️",
            checkbox: "☑️",
        };
        return iconMap[type] || "🔘";
    };

    const getStepDescription = (type) => {
        const description = options.find(
            (option) => option.type === type,
        )?.description;
        return description || "Generic step component";
    };

    const stepTypes = options.map((option) => ({
        value: option.type,
        label: option.name,
        icon: getStepIcon(option.type),
        description: getStepDescription(option.type),
    }));

    const handleSectionToggle = useCallback((section) => {
        setExpandedSections((prev) => ({
            ...prev,
            [section]: !prev[section],
        }));
    }, []);

    const handleContextMenu = useCallback((event, stepId) => {
        event.preventDefault();
        setAnchorEl(event.currentTarget);
        setContextStepId(stepId);
    }, []);

    const handleContextMenuClose = useCallback(() => {
        setAnchorEl(null);
        setContextStepId(null);
    }, []);

    const handleAddStep = useCallback(() => {
        try {
            if (!newStepType) {
                setError("Please select a step type");
                return;
            }

            if (!onAddStep) {
                setError("Add step function not available");
                return;
            }

            onAddStep(newStepType);
            setAddStepDialog(false);
            setNewStepType("button");
            setError(null);
        } catch (error) {
            console.error("Error adding step:", error);
            setError("Failed to add step");
        }
    }, [newStepType, onAddStep]);

    const handleEditStep = useCallback(
        (stepId) => {
            try {
                if (!onEditStep) {
                    setError("Edit step function not available");
                    return;
                }
                onEditStep(stepId);
                handleContextMenuClose();
            } catch (error) {
                console.error("Error editing step:", error);
                setError("Failed to edit step");
            }
        },
        [onEditStep, handleContextMenuClose],
    );

    const handleDeleteStep = useCallback(
        (stepId) => {
            try {
                if (!onDeleteStep) {
                    setError("Delete step function not available");
                    return;
                }
                onDeleteStep(stepId);
                handleContextMenuClose();
            } catch (error) {
                console.error("Error deleting step:", error);
                setError("Failed to delete step");
            }
        },
        [onDeleteStep, handleContextMenuClose],
    );

    const handleDuplicateStep = useCallback(
        (stepId) => {
            try {
                if (!onDuplicateStep) {
                    setError("Duplicate step function not available");
                    return;
                }
                onDuplicateStep(stepId);
                handleContextMenuClose();
            } catch (error) {
                console.error("Error duplicating step:", error);
                setError("Failed to duplicate step");
            }
        },
        [onDuplicateStep, handleContextMenuClose],
    );

    const handleSelectStep = useCallback(
        (stepId) => {
            try {
                if (!onSelectStep) {
                    setError("Select step function not available");
                    return;
                }
                onSelectStep(stepId);
            } catch (error) {
                console.error("Error selecting step:", error);
                setError("Failed to select step");
            }
        },
        [onSelectStep],
    );

    const getStepStatus = useCallback((step) => {
        if (!step.title) return "incomplete";
        if (step.required && !step.attributes) return "incomplete";
        return "complete";
    }, []);

    const getStatusColor = useCallback((status) => {
        switch (status) {
            case "complete":
                return "success";
            case "incomplete":
                return "error";
            default:
                return "default";
        }
    }, []);

    return (
        <Box
            sx={{
                height: "100%",
                display: "flex",
                flexDirection: "column",
                bgcolor: "background.paper",
                borderRight: "1px solid",
                borderColor: "divider",
            }}
        >
            {/* Header */}
            <Box
                sx={{ p: 2, borderBottom: "1px solid", borderColor: "divider" }}
            >
                <Box
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        mb: 1,
                    }}
                >
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                        Funnel Steps
                    </Typography>
                    <Chip
                        label={steps.length}
                        size="small"
                        color="warnings"
                        variant="outlined"
                        onClick={() => {}}
                    />
                </Box>

                <Box sx={{ display: "flex", gap: 1, mb: 2 }}>
                    <Button
                        startIcon={<AddIcon />}
                        variant="contained"
                        color="primary"
                        onClick={() => setAddStepDialog(true)}
                        size="small"
                        style={{
                            minWidth: "unset !important",
                        }}
                    >
                        Add Step
                    </Button>
                </Box>

                {error && (
                    <Alert
                        severity="error"
                        sx={{ mb: 2 }}
                        onClose={() => setError(null)}
                    >
                        {error}
                    </Alert>
                )}
            </Box>

            {/* Steps List */}
            <Box
                sx={{ flex: 1, height: "100%", overflowX: "hidden" }}
                className="funnel-scrollbar"
            >
                <List
                    sx={{
                        p: 0,
                        height: "100%",
                        overflowX: "hidden",
                    }}
                >
                    <ListItem
                        button
                        onClick={() => handleSectionToggle("steps")}
                        sx={{
                            bgcolor: "grey.50",
                            borderBottom: "1px solid",
                            borderColor: "divider",
                            cursor: "pointer",
                        }}
                    >
                        <ListItemText
                            primary="Steps"
                            secondary={`${steps.length} steps configured`}
                        />
                        {expandedSections.steps ? (
                            <CollapseIcon />
                        ) : (
                            <ExpandIcon />
                        )}
                    </ListItem>

                    <Collapse
                        in={expandedSections.steps}
                        timeout="auto"
                        unmountOnExit
                    >
                        <Box>
                            {steps.length === 0 ? (
                                <Box
                                    sx={{
                                        p: 2,
                                        textAlign: "center",
                                        color: "text.secondary",
                                    }}
                                >
                                    <Typography variant="body2">
                                        No steps added yet
                                    </Typography>
                                    <Typography
                                        variant="caption"
                                        color="text.disabled"
                                    >
                                        Click "Add Step" to get started
                                    </Typography>
                                </Box>
                            ) : (
                                <DndContext
                                    sensors={sensors}
                                    collisionDetection={closestCenter}
                                    onDragEnd={handleDragEnd}
                                >
                                    <SortableContext
                                        items={steps.map((step) => step.id)}
                                        strategy={verticalListSortingStrategy}
                                    >
                                        {steps.map((step, index) => {
                                            const status = getStepStatus(step);
                                            const isSelected =
                                                selectedStepId === step.id;

                                            return (
                                                <SortableItem
                                                    key={step.id}
                                                    step={step}
                                                    index={index}
                                                    isSelected={isSelected}
                                                    status={status}
                                                    getStatusColor={
                                                        getStatusColor
                                                    }
                                                    handleSelectStep={
                                                        handleSelectStep
                                                    }
                                                    handleContextMenu={
                                                        handleContextMenu
                                                    }
                                                />
                                            );
                                        })}
                                    </SortableContext>
                                </DndContext>
                            )}
                        </Box>
                    </Collapse>
                </List>
            </Box>

            {/* Context Menu */}
            <Menu
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={handleContextMenuClose}
            >
                <MenuItem
                    onClick={() => handleEditStep(contextStepId)}
                    sx={{ justifyContent: "flex-start" }}
                >
                    <EditIcon fontSize="small" sx={{ mr: 1 }} />
                    <Box textAlign="left">Edit</Box>
                </MenuItem>
                <MenuItem
                    onClick={() => handleDuplicateStep(contextStepId)}
                    sx={{ textAlign: "left" }}
                >
                    <CopyIcon fontSize="small" sx={{ mr: 1 }} />
                    <Box textAlign="left">Duplicate</Box>
                </MenuItem>
                <MenuItem
                    onClick={() => handleDeleteStep(contextStepId)}
                    sx={{ color: "error.main" }}
                >
                    <DeleteIcon fontSize="small" sx={{ mr: 1 }} />
                    <Box textAlign="left">Delete</Box>
                </MenuItem>
            </Menu>

            {/* Add Step Dialog */}
            <Dialog
                open={addStepDialog}
                onClose={() => setAddStepDialog(false)}
                maxWidth="sm"
                fullWidth
                fullScreen={isMobile}
            >
                <DialogTitle>Add New Step</DialogTitle>
                <DialogContent>
                    <FormControl fullWidth sx={{ mt: 2 }}>
                        <InputLabel>Step Type</InputLabel>
                        <Select
                            value={newStepType}
                            onChange={(e) => setNewStepType(e.target.value)}
                            label="Step Type"
                        >
                            {stepTypes.map((type) => (
                                <MenuItem key={type.value} value={type.value}>
                                    <Box
                                        sx={{
                                            display: "flex",
                                            alignItems: "center",
                                            gap: 1,
                                        }}
                                    >
                                        <Box>
                                            <Typography variant="body2">
                                                {type.label}
                                            </Typography>
                                            <Typography
                                                variant="caption"
                                                color="text.secondary"
                                            >
                                                {type.description}
                                            </Typography>
                                        </Box>
                                    </Box>
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </DialogContent>
                <DialogActions>
                    <Button
                        onClick={() => setAddStepDialog(false)}
                        color="primary"
                    >
                        Cancel
                    </Button>
                    <Button
                        onClick={handleAddStep}
                        variant="contained"
                        color="primary"
                    >
                        Add Step
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default StepManager;
